import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Zap } from "lucide-react";

export default function AbcTheoryStep({ onDone }) {
  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[var(--ha-text)]">El Modelo ABC</h2>
      <p className="mt-3 text-[var(--ha-muted)] leading-relaxed max-w-2xl">
        Los eventos activadores no producen directamente las consecuencias. Son <strong>tus creencias</strong> las que
        interactúan con los eventos y conducen a los sentimientos y comportamientos.
      </p>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        {[
          { letter: "A", title: "Evento activador", body: "Situaciones específicas, externas o internas, que ocurren en tu día a día.", color: "var(--ha-accent)" },
          { letter: "B", title: "Creencias / pensamientos", body: "Cómo interpretas ese evento. Racionales o irracionales.", color: "var(--ha-primary)" },
          { letter: "C", title: "Consecuencias", body: "Las emociones, comportamientos y reacciones corporales que aparecen.", color: "var(--ha-secondary)" },
        ].map((c) => (
          <Card key={c.letter} className="rounded-2xl border-[var(--ha-border)] bg-white">
            <CardContent className="p-6">
              <div className="h-10 w-10 rounded-2xl grid place-items-center text-white font-bold" style={{ backgroundColor: c.color }}>
                {c.letter}
              </div>
              <p className="mt-4 font-semibold text-[var(--ha-text)]">{c.title}</p>
              <p className="mt-2 text-sm text-[var(--ha-muted)] leading-relaxed">{c.body}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-[var(--ha-border)] bg-white p-6">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-[var(--ha-secondary)] mt-1" strokeWidth={1.75} />
          <div>
            <h3 className="font-semibold text-[var(--ha-text)]">Misma situación, distintos pensamientos, distintas emociones</h3>
            <p className="mt-2 text-sm text-[var(--ha-muted)] leading-relaxed">
              Podemos <strong>controlar los pensamientos</strong> sobre una situación y, con ello, las emociones y el
              comportamiento que provoca. Es más importante cómo interpretamos la situación que la situación en sí.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[var(--ha-error)]/25 bg-[var(--ha-error)]/5 p-5">
          <p className="text-xs uppercase font-bold tracking-[0.15em] text-[var(--ha-error)]">Ejemplo · Persona 1</p>
          <p className="mt-3 font-semibold text-[var(--ha-text)]">A · Mi madre está en el hospital por dolor abdominal.</p>
          <p className="mt-2 text-sm text-[var(--ha-muted)]">B · {"«Seguro que es algo malo y la tienen que operar. Puede morir.»"}</p>
          <p className="mt-2 text-sm text-[var(--ha-text)] font-semibold">C · Tristeza, preocupación, ansiedad.</p>
        </div>
        <div className="rounded-2xl border border-[var(--ha-success)]/25 bg-[var(--ha-success)]/5 p-5">
          <p className="text-xs uppercase font-bold tracking-[0.15em] text-[var(--ha-success)]">Ejemplo · Persona 2</p>
          <p className="mt-3 font-semibold text-[var(--ha-text)]">A · Mi madre está en el hospital por dolor abdominal.</p>
          <p className="mt-2 text-sm text-[var(--ha-muted)]">B · {"«Está en buenas manos. Le solucionarán el problema.»"}</p>
          <p className="mt-2 text-sm text-[var(--ha-text)] font-semibold">C · Tranquilidad, optimismo.</p>
        </div>
      </div>

      <div className="mt-10 flex justify-end">
        <Button
          data-testid="abc-theory-continue-btn"
          onClick={onDone}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
        >
          Practicar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
